import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import '../Blog.css'; // Import blog styles for accurate preview

// Configure marked for safe HTML output
marked.setOptions({
  breaks: true,
  gfm: true
});

const CATEGORIES = [
  { value: 'invoicing', label: 'Invoicing Basics & Best Practices' },
  { value: 'bill-of-sale', label: 'Bill of Sale Articles' },
  { value: 'online-safety', label: 'Online Safety & Scam Prevention' },
  { value: 'buying-selling', label: 'Buying and Selling Tips' },
  { value: 'business', label: 'General Business Advice' },
  { value: 'thrifting', label: 'Thrifting to Marketplace Pipeline' }
];

const AdminBlogEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [useMarkdown, setUseMarkdown] = useState(true);
  const [markdownContent, setMarkdownContent] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: 'buying-selling',
    metaDescription: '',
    content: '',
    excerpt: '',
    featuredImage: { url: '', alt: '' },
    author: { name: 'PrivateSold Team' },
    status: 'draft',
    publishDate: '',
    tags: '',
    seoTitle: '',
    canonicalUrl: '',
    // Premium PDF fields
    hasPremiumPdf: false,
    premiumTitle: '',
    premiumDescription: '',
    premiumPrice: 7.99,
    premiumPdfUrl: '',
    premiumPreviewPoints: ''
  });

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await api.get(`/blog/admin/${id}`);
        const post = response.data;
        
        setFormData({
          title: post.title || '',
          slug: post.slug || '',
          category: post.category || 'tips',
          metaDescription: post.metaDescription || '',
          content: post.content || '',
          excerpt: post.excerpt || '',
          featuredImage: post.featuredImage || { url: '', alt: '' },
          author: post.author || { name: 'PrivateSold Team' },
          status: post.status || 'draft',
          publishDate: post.publishDate ? new Date(post.publishDate).toISOString().slice(0, 16) : '',
          tags: post.tags?.join(', ') || '',
          seoTitle: post.seoTitle || '',
          canonicalUrl: post.canonicalUrl || '',
          // Premium PDF fields
          hasPremiumPdf: post.hasPremiumPdf || false,
          premiumTitle: post.premiumTitle || '',
          premiumDescription: post.premiumDescription || '',
          premiumPrice: post.premiumPrice || 7.99,
          premiumPdfUrl: post.premiumPdfUrl || '',
          premiumPreviewPoints: post.premiumPreviewPoints?.join('\n') || ''
        });
        
        // Load markdown content if it exists
        if (post.markdownContent) {
          setMarkdownContent(post.markdownContent);
          setUseMarkdown(true);
        }
      } catch (err) {
        setError('Failed to load post');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (!isNew) {
      fetchPost();
    }
  }, [id, isNew]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const generateSlug = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setFormData(prev => ({ ...prev, slug }));
  };

  const handleSubmit = async (e, saveStatus) => {
    e?.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // If using markdown, auto-convert to HTML before saving
      let finalContent = formData.content;
      if (useMarkdown && markdownContent) {
        finalContent = marked(markdownContent);
      }

      const payload = {
        ...formData,
        content: finalContent,
        markdownContent: markdownContent || '', // Always preserve markdown if it exists
        status: saveStatus || formData.status,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        publishDate: formData.publishDate || (saveStatus === 'published' ? new Date().toISOString() : null),
        // Premium PDF: convert preview points from newline-separated to array
        premiumPreviewPoints: formData.premiumPreviewPoints 
          ? formData.premiumPreviewPoints.split('\n').map(p => p.trim()).filter(Boolean)
          : []
      };

      if (isNew) {
        const response = await api.post('/blog/admin', payload);
        navigate(`/admin/blog/${response.data._id}`);
      } else {
        await api.put(`/blog/admin/${id}`, payload);
      }

      if (saveStatus === 'published') {
        const isScheduled = formData.publishDate && new Date(formData.publishDate) > new Date();
        if (isScheduled) {
          alert(`Post scheduled for ${new Date(formData.publishDate).toLocaleString()}`);
        } else {
          alert('Post published successfully!');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save post');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublish = async () => {
    const reason = prompt('Why are you unpublishing this article?\n\nThis will be logged for reference.');
    
    if (reason === null) return; // User cancelled
    if (!reason.trim()) {
      alert('Please provide a reason for unpublishing.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await api.put(`/blog/admin/${id}`, {
        ...formData,
        status: 'draft',
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        unpublishReason: reason,
        unpublishedAt: new Date().toISOString()
      });

      setFormData(prev => ({ ...prev, status: 'draft' }));
      alert('Post unpublished and saved as draft.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to unpublish post');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">Loading post...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="blog-editor">
        <div className="editor-header">
          <div className="editor-header-left">
            <button onClick={() => navigate('/admin/blog')} className="back-btn">
              ← Back to Posts
            </button>
            <h1>{isNew ? 'New Post' : 'Edit Post'}</h1>
          </div>
          <div className="editor-header-right">
            <button 
              onClick={() => setPreviewMode(!previewMode)}
              className="preview-btn"
            >
              {previewMode ? 'Edit' : 'Preview'}
            </button>
            {formData.status !== 'published' && (
              <button 
                onClick={(e) => handleSubmit(e, 'draft')}
                disabled={saving}
                className="save-btn"
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
            )}
            <button 
              onClick={(e) => handleSubmit(e, 'published')}
              disabled={saving}
              className="publish-btn"
            >
              {saving ? 'Saving...' : (
                formData.status === 'published' ? 'Update' : 
                (formData.publishDate && new Date(formData.publishDate) > new Date() ? 'Schedule' : 'Publish Now')
              )}
            </button>
            {formData.status === 'published' && (
              <button 
                onClick={handleUnpublish}
                disabled={saving}
                className="unpublish-btn"
                style={{ backgroundColor: '#dc2626', marginLeft: '0.5rem' }}
              >
                {saving ? 'Saving...' : 'Unpublish'}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="editor-error">{error}</div>
        )}

        {previewMode ? (
          <div className="preview-modal-overlay" onClick={() => setPreviewMode(false)}>
            <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
              <div className="preview-modal-header">
                <span>Preview - This is how your post will look</span>
                <button onClick={() => setPreviewMode(false)} className="preview-close-btn">✕ Close</button>
              </div>
              <div className="preview-modal-content blog-post-page">
                <article className="blog-post-main" style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
                  {formData.featuredImage.url && (
                    <div className="blog-post-featured-image">
                      <img src={formData.featuredImage.url} alt={formData.featuredImage.alt} />
                    </div>
                  )}
                  <div className="blog-post-header">
                    <div className="blog-post-meta">
                      <span className="blog-post-category">{CATEGORIES.find(c => c.value === formData.category)?.label}</span>
                      <span className="blog-post-date">{new Date().toLocaleDateString()}</span>
                    </div>
                    <h1>{formData.title || 'Untitled Post'}</h1>
                    <div className="blog-post-author">
                      <span>By {formData.author?.name || 'PrivateSold Team'}</span>
                    </div>
                  </div>
                  <div 
                    className="blog-post-content"
                    dangerouslySetInnerHTML={{ __html: useMarkdown && markdownContent ? marked(markdownContent) : (formData.content || '<p>No content yet...</p>') }}
                  />
                </article>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="editor-form">
            <div className="editor-main">
              {/* Title */}
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter post title..."
                  required
                  className="form-input title-input"
                />
              </div>

              {/* Slug */}
              <div className="form-group">
                <label htmlFor="slug">
                  URL Slug *
                  <button type="button" onClick={generateSlug} className="generate-btn">
                    Generate from title
                  </button>
                </label>
                <div className="slug-preview">
                  <span>privatesold.com/blog/</span>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    placeholder="url-slug"
                    required
                    className="form-input slug-input"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="form-group">
                <label htmlFor="content">
                  Content *
                  <div className="content-mode-toggle">
                    <button 
                      type="button" 
                      onClick={() => setUseMarkdown(false)}
                      className={!useMarkdown ? 'active' : ''}
                    >
                      HTML
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setUseMarkdown(true)}
                      className={useMarkdown ? 'active' : ''}
                    >
                      Markdown
                    </button>
                    {useMarkdown && (
                      <button 
                        type="button"
                        onClick={() => {
                          const html = marked(markdownContent);
                          setFormData(prev => ({ ...prev, content: html }));
                        }}
                        className="convert-btn"
                      >
                        Convert to HTML ↓
                      </button>
                    )}
                  </div>
                </label>
                {useMarkdown ? (
                  <>
                    <textarea
                      id="markdown"
                      value={markdownContent}
                      onChange={(e) => setMarkdownContent(e.target.value)}
                      placeholder="# Your Title&#10;&#10;Write in **markdown** format..."
                      className="form-input content-input"
                      rows={20}
                    />
                    <span className="label-hint">Write in Markdown - auto-converts to HTML when saving</span>
                  </>
                ) : (
                  <textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    placeholder="<p>Your content here...</p>"
                    required
                    className="form-input content-input"
                    rows={20}
                  />
                )}
              </div>

              {/* Excerpt */}
              <div className="form-group">
                <label htmlFor="excerpt">
                  Excerpt
                  <span className="label-hint">Optional - auto-generated if empty</span>
                </label>
                <textarea
                  id="excerpt"
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleChange}
                  placeholder="Brief summary for post previews..."
                  className="form-input"
                  rows={3}
                  maxLength={300}
                />
                <span className="char-count">{formData.excerpt.length}/300</span>
              </div>
            </div>

            <div className="editor-sidebar">
              {/* Status */}
              <div className="sidebar-card">
                <h3>Status</h3>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="form-input"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>

                {/* Publish Date */}
                <label htmlFor="publishDate" style={{ marginTop: '1rem', display: 'block' }}>
                  Publish Date
                </label>
                <input
                  type="datetime-local"
                  id="publishDate"
                  name="publishDate"
                  value={formData.publishDate}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              {/* Category */}
              <div className="sidebar-card">
                <h3>Category</h3>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="form-input"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Featured Image */}
              <div className="sidebar-card">
                <h3>Featured Image</h3>
                <input
                  type="url"
                  name="featuredImage.url"
                  value={formData.featuredImage.url}
                  onChange={handleChange}
                  placeholder="Image URL"
                  className="form-input"
                />
                <input
                  type="text"
                  name="featuredImage.alt"
                  value={formData.featuredImage.alt}
                  onChange={handleChange}
                  placeholder="Alt text"
                  className="form-input"
                  style={{ marginTop: '0.5rem' }}
                />
                {formData.featuredImage.url && (
                  <img 
                    src={formData.featuredImage.url} 
                    alt="Preview" 
                    className="image-preview"
                  />
                )}
              </div>

              {/* Author */}
              <div className="sidebar-card">
                <h3>Author</h3>
                <input
                  type="text"
                  name="author.name"
                  value={formData.author.name}
                  onChange={handleChange}
                  placeholder="Author name"
                  className="form-input"
                />
              </div>

              {/* Tags */}
              <div className="sidebar-card">
                <h3>Tags</h3>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  placeholder="tag1, tag2, tag3"
                  className="form-input"
                />
                <span className="hint">Comma-separated</span>
              </div>

              {/* SEO */}
              <div className="sidebar-card">
                <h3>SEO</h3>
                <label htmlFor="seoTitle">SEO Title</label>
                <input
                  type="text"
                  id="seoTitle"
                  name="seoTitle"
                  value={formData.seoTitle}
                  onChange={handleChange}
                  placeholder="Override page title"
                  className="form-input"
                />

                <label htmlFor="metaDescription" style={{ marginTop: '0.75rem', display: 'block' }}>
                  Meta Description
                </label>
                <textarea
                  id="metaDescription"
                  name="metaDescription"
                  value={formData.metaDescription}
                  onChange={handleChange}
                  placeholder="155-160 characters recommended"
                  className="form-input"
                  rows={3}
                  maxLength={160}
                />
                <span className="char-count">{formData.metaDescription.length}/160</span>

                <label htmlFor="canonicalUrl" style={{ marginTop: '0.75rem', display: 'block' }}>
                  Canonical URL
                </label>
                <input
                  type="url"
                  id="canonicalUrl"
                  name="canonicalUrl"
                  value={formData.canonicalUrl}
                  onChange={handleChange}
                  placeholder="Original URL if republished"
                  className="form-input"
                />
              </div>

              {/* Premium PDF Guide */}
              <div className="sidebar-card premium-pdf-section">
                <h3>💰 Premium PDF</h3>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="hasPremiumPdf"
                    checked={formData.hasPremiumPdf || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, hasPremiumPdf: e.target.checked }))}
                  />
                  <span>Has Premium PDF Guide</span>
                </label>

                {formData.hasPremiumPdf && (
                  <>
                    <label htmlFor="premiumTitle" style={{ marginTop: '1rem', display: 'block' }}>
                      Premium Title
                    </label>
                    <input
                      type="text"
                      id="premiumTitle"
                      name="premiumTitle"
                      value={formData.premiumTitle || ''}
                      onChange={handleChange}
                      placeholder="The Complete Guide to..."
                      className="form-input"
                    />

                    <label htmlFor="premiumDescription" style={{ marginTop: '0.75rem', display: 'block' }}>
                      Sales Description
                    </label>
                    <textarea
                      id="premiumDescription"
                      name="premiumDescription"
                      value={formData.premiumDescription || ''}
                      onChange={handleChange}
                      placeholder="Why should people buy this guide?"
                      className="form-input"
                      rows={3}
                    />

                    <label htmlFor="premiumPrice" style={{ marginTop: '0.75rem', display: 'block' }}>
                      Price ($)
                    </label>
                    <input
                      type="number"
                      id="premiumPrice"
                      name="premiumPrice"
                      value={formData.premiumPrice || 7.99}
                      onChange={handleChange}
                      min="0.99"
                      max="99.99"
                      step="0.01"
                      className="form-input"
                    />

                    <label htmlFor="premiumPdfUrl" style={{ marginTop: '0.75rem', display: 'block' }}>
                      PDF URL
                    </label>
                    <input
                      type="url"
                      id="premiumPdfUrl"
                      name="premiumPdfUrl"
                      value={formData.premiumPdfUrl || ''}
                      onChange={handleChange}
                      placeholder="URL to the PDF file"
                      className="form-input"
                    />

                    <label htmlFor="premiumPreviewPoints" style={{ marginTop: '0.75rem', display: 'block' }}>
                      Preview Points
                    </label>
                    <textarea
                      id="premiumPreviewPoints"
                      name="premiumPreviewPoints"
                      value={formData.premiumPreviewPoints || ''}
                      onChange={handleChange}
                      placeholder="What's included (one per line):&#10;Complete legal checklist&#10;State-by-state requirements&#10;10+ templates"
                      className="form-input"
                      rows={4}
                    />
                    <span className="hint">One bullet point per line</span>
                  </>
                )}
              </div>
            </div>
          </form>
        )}
      </div>

      <style>{`
        .blog-editor {
          max-width: 1400px;
        }
        .editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .editor-header-left {
          display: flex;
          align-items: center;
          gap: 1.5rem;
        }
        .editor-header-left h1 {
          margin: 0;
          font-size: 1.5rem;
        }
        .editor-header-right {
          display: flex;
          gap: 0.75rem;
        }
        .back-btn {
          background: none;
          border: none;
          color: var(--primary-color);
          cursor: pointer;
          padding: 0;
          font-size: 1rem;
        }
        .back-btn:hover {
          text-decoration: underline;
        }
        .preview-btn, .save-btn, .publish-btn {
          padding: 0.5rem 1.25rem;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          border: 1px solid;
        }
        .preview-btn {
          background: white;
          border-color: #e5e7eb;
          color: #374151;
        }
        .save-btn {
          background: white;
          border-color: var(--primary-color);
          color: var(--primary-color);
        }
        .publish-btn {
          background: var(--primary-color);
          border-color: var(--primary-color);
          color: white;
        }
        .publish-btn:hover {
          background: var(--primary-hover);
        }
        .editor-error {
          background: #fef2f2;
          color: #dc2626;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
        }
        .editor-form {
          display: grid;
          grid-template-columns: 1fr 320px;
          gap: 2rem;
        }
        .editor-main, .editor-preview {
          background: white;
          padding: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .editor-sidebar {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .sidebar-card {
          background: white;
          padding: 1.25rem;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .sidebar-card h3 {
          margin: 0 0 0.75rem;
          font-size: 0.95rem;
          color: #374151;
        }
        .form-group {
          margin-bottom: 1.5rem;
        }
        .form-group label {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: 500;
          margin-bottom: 0.5rem;
          color: #374151;
        }
        .label-hint {
          font-weight: normal;
          font-size: 0.85rem;
          color: #9ca3af;
        }
        .form-input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 1rem;
          box-sizing: border-box;
        }
        .form-input:focus {
          outline: none;
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }
        .title-input {
          font-size: 1.25rem;
          font-weight: 500;
        }
        .slug-preview {
          display: flex;
          align-items: center;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding-left: 0.75rem;
        }
        .slug-preview span {
          color: #9ca3af;
          font-size: 0.9rem;
        }
        .slug-input {
          border: none;
          background: transparent;
          flex: 1;
        }
        .slug-input:focus {
          box-shadow: none;
        }
        .generate-btn {
          background: none;
          border: none;
          color: var(--primary-color);
          font-size: 0.85rem;
          cursor: pointer;
          padding: 0;
        }
        .generate-btn:hover {
          text-decoration: underline;
        }
        .content-mode-toggle {
          display: flex;
          gap: 0.5rem;
          margin-left: auto;
        }
        .content-mode-toggle button {
          padding: 0.25rem 0.75rem;
          border: 1px solid #e5e7eb;
          background: white;
          border-radius: 4px;
          font-size: 0.8rem;
          cursor: pointer;
        }
        .content-mode-toggle button.active {
          background: var(--primary-color);
          border-color: var(--primary-color);
          color: white;
        }
        .content-mode-toggle .convert-btn {
          background: #059669;
          border-color: #059669;
          color: white;
        }
        .content-input {
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.9rem;
          line-height: 1.6;
        }
        .char-count {
          font-size: 0.8rem;
          color: #9ca3af;
          float: right;
          margin-top: 0.25rem;
        }
        .hint {
          font-size: 0.8rem;
          color: #9ca3af;
          display: block;
          margin-top: 0.25rem;
        }
        .image-preview {
          width: 100%;
          height: 120px;
          object-fit: cover;
          border-radius: 8px;
          margin-top: 0.75rem;
        }
        
        /* Preview Mode */
        .editor-preview {
          max-width: 800px;
        }
        .preview-image {
          width: 100%;
          max-height: 400px;
          object-fit: cover;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }
        .preview-content h1 {
          font-size: 2rem;
          margin-bottom: 1rem;
        }
        .preview-meta {
          display: flex;
          gap: 1rem;
          color: #6b7280;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e5e7eb;
        }
        .preview-body {
          font-size: 1.1rem;
          line-height: 1.8;
        }
        .preview-body h2 {
          margin-top: 2rem;
        }
        .preview-body p {
          margin-bottom: 1rem;
        }
        
        @media (max-width: 900px) {
          .editor-form {
            grid-template-columns: 1fr;
          }
          .editor-header {
            flex-direction: column;
            align-items: flex-start;
          }
        }
        
        /* Premium PDF Section */
        .premium-pdf-section {
          border: 2px solid #10b981;
          background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
        }
        .premium-pdf-section h3 {
          color: #059669;
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-weight: 500;
        }
        .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }
      `}</style>
    </AdminLayout>
  );
};

export default AdminBlogEdit;
